/**
 * WorkHistorySection Component
 *
 * Modern table design for work history with detailed information
 * Includes View History popup functionality
 */

import React, { useState } from "react";
import { HiBriefcase, HiEye } from "react-icons/hi";
import CollapsibleSection from "../components/CollapsibleSection";
import ShowEmployeeDetails from "../../ExitFromUserDetails/showDetailsPopUp";

const WorkHistorySection = ({ data }) => {
  console.log({data})
  const [showPopup, setShowPopup] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const getDaysBetweenDates = (date1, date2) => {
    const firstDate = date1 ? new Date(date1) : new Date();
    const secondDate = date2 ? new Date(date2) : new Date();
    const differenceInMilliseconds = secondDate - firstDate;
    const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
    return differenceInDays;
  };

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "-----";
    return new Date(isoDate)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .split(" ")
      .join("-")
      .toUpperCase();
  };


  const handleViewHistory = (history) => {
    setSelectedHistory({history, arrival_date: data?.data?.[0]?.arrival_date});
    setShowPopup(true);
  };

  const rawIdleHistory = data?.idleHistory || [];

  // Remove duplicates based on journey_id (unique identifier)
  const idleHistory = rawIdleHistory.filter((history, index, self) =>
    index === self.findIndex((h) => h.client_id === history.client_id)
  );


  console.log("rawHistory:", rawIdleHistory);
console.log("rawHistory length:", rawIdleHistory?.length);

const latestHistory = Object.values(
  (rawIdleHistory || []).reduce((acc, history) => {
    const key = `${history.employee_id}_${history.client_id}`;

    if (!acc[key]) acc[key] = [];
    acc[key].push(history);

    return acc;
  }, {})
)
  .map((records) => {
    // If only 1 record, return it as-is
    if (records.length === 1) {
      return records[0];
    }

    // Multiple records - group by client_id
    const firstRecord = [...records]
      .filter((h) => h.mobilization_date)
      .sort(
        (a, b) =>
          new Date(a.mobilization_date) -
          new Date(b.mobilization_date)
      )[0];

    // Find latest demob date (or second-last if last one is null)
    const recordsWithDemob = [...records]
      .filter((h) => h.demobilization_date)
      .sort(
        (a, b) =>
          new Date(b.demobilization_date) -
          new Date(a.demobilization_date)
      );

    const latestRecord = recordsWithDemob[0] || records[records.length - 1];

    if (!firstRecord) return null;

    return {
      ...latestRecord,
      mobilization_date: firstRecord.mobilization_date,
    };
  })
  .filter(Boolean);

console.log("Grouped history:", latestHistory);

  const columns = [
    { key: "sequence", label: "SEQ", width: "w-16" },
    { key: "client", label: "CLIENT NAME", width: "w-48" },
    { key: "contractType", label: "CONTRACT TYPE", width: "w-32" },
    { key: "basicSalary", label: "BASIC SALARY", width: "w-28" },
    { key: "totalSalary", label: "TOTAL SALARY", width: "w-28" },
    { key: "mobilizationDate", label: "MOB. DATE", width: "w-32" },
    { key: "demobilizationDate", label: "DEMOB. DATE", width: "w-32" },
    { key: "daysWorked", label: "DAYS WORKED", width: "w-28" },
    { key: "daysAbsent", label: "ABSENT", width: "w-24" },
    { key: "total", label: "TOTAL", width: "w-24" },
    { key: "noDays", label: "NO. DAYS", width: "w-24" },
    { key: "actions", label: "ACTIONS", width: "w-32" },
  ];

  return (
    <>
      <CollapsibleSection
        title="WORK HISTORY"
        icon={<HiBriefcase className="w-5 h-5" />}
        badge={latestHistory.length > 0 ? `${latestHistory.length} Projects` : null}
        defaultCollapsed={true}
      >
        {latestHistory.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No work history available
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              {/* Table Header */}
              <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider ${column.width}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-gray-100">
                {latestHistory.map((history, index) => {
                  const daysWorked = getDaysBetweenDates(
                    history.mobilization_date,
                    history.demobilization_date
                  );

                  return (
                    <tr
                      key={history.id || index}
                      className="hover:bg-blue-50 transition-colors duration-150"
                    >
                      {/* Sequence */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-semibold text-xs">
                          {index + 1}
                        </span>
                      </td>

                      {/* Client Name */}
                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {history.project_name || "-"}
                        </div>
                      </td>

                      {/* history Type */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {history.quotation_type || "-"}
                        </span>
                      </td>

                      {/* Basic Salary */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          {history.basic || "0"}
                        </div>
                      </td>

                      {/* Total Salary */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-700">
                          {history.totalsalary || "0"}
                        </div>
                      </td>

                      {/* Mobilization Date */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-700 font-medium">
                          {formatDisplayDate(history.mobilization_date)}
                        </div>
                      </td>

                      {/* Demobilization Date */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-700 font-medium">
                          {formatDisplayDate(history.demobilization_date)}
                        </div>
                      </td>

                      {/* Days Worked */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {daysWorked}
                        </span>
                      </td>

                      {/* Days Absent */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                          0
                        </span>
                      </td>

                      {/* Total */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {daysWorked}
                        </span>
                      </td>

                      {/* No. of Days */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                          {daysWorked}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewHistory(rawIdleHistory.filter((h) => h.client_id === history.client_id))}
                          className="inline-flex items-center px-3 py-1.5 bg-btn-red text-white text-xs font-semibold rounded-lg hover:bg-btn-red-hover focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-150 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <HiEye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      {/* View History Popup */}
      {showPopup && selectedHistory && (
        <ShowEmployeeDetails
          PopUp={showPopup}
          setPopUp={setShowPopup}
          propData={selectedHistory}
        />
      )}
    </>
  );
};

export default WorkHistorySection;
